import React, { useEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import terracycleIcon from '../../../assets/images/pages/referrals/terracycleIcon.svg'

export default function ReferralInfo({
  translations,
  userData,
  userIsReferrer,
}) {
  const inputRef = useRef(null)
  const [copied, setCopied] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(false)
  const [isWebShareSupported, setIsWebShareSupported] = useState(false)

  const {
    available_store_credit_referral_amount,
    minimum_order_amount,
    expiring_store_credit_referral_amount,
    expiring_store_credit_referral_due_date,
    completed_referral_orders,
    user_referral_link,
  } = userData

  const {
    minimum_order,
    store_credit_title,
    shop_now_link,
    shop_now_title,
    store_credits_expiring,
    none_store_credit_expiring,
    store_credit_expiring_hint,
    completed_referrals_order_count_title,
    statistic_section_title,
    statistic_section_content,
    your_referral_link_title,
    copy_link,
    social_media_share_link_title,
  } = translations

  const useShareButton = !isLargeScreen && isWebShareSupported
  const showSocialSection =
    isLargeScreen || (!isLargeScreen && !isWebShareSupported)

  useEffect(() => {
    const largeScreen = typeof window !== 'undefined' && window.innerWidth > 991
    const webShareSupported =
      typeof navigator !== 'undefined' && typeof navigator.share === 'function'

    setIsLargeScreen(largeScreen)
    setIsWebShareSupported(webShareSupported)

    if (typeof initTooltip === 'function') {
      initTooltip()
    }
  }, [])

  const handleCopy = async () => {
    try {
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(user_referral_link)
      } else {
        // Fallback using execCommand
        if (inputRef.current) {
          inputRef.current.select()
          document.execCommand('copy')
        } else {
          throw new Error('No fallback available to copy text.')
        }
      }

      setCopied(true)
      $.gritter.add({
        fade: true,
        text: 'Your referral link has been copied!',
        time: '2000',
        title: 'Notification',
      })
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy!', err)
    }
  }

  const handleMobileShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ url: user_referral_link })
      }
    } catch (error) {
      console.error('Error sharing via mobile:', error)
    }
  }

  const StoreCreditBlock = () => (
    <div className="columnLeftSide__green-block">
      <h4 className="text-center d-xl-none mb-0">{store_credit_title}</h4>
      <div className="text-xl-end mb-5 mb-xl-0">
        <div className="h1 mb-0">{available_store_credit_referral_amount}</div>
        <h5 className="mb-4">
          {minimum_order}: {minimum_order_amount}
        </h5>
      </div>
      <div className="text-start">
        <h4 className="mb-4 d-none d-xl-block">{store_credit_title}</h4>
        <a
          className="buttonComponent--secondary buttonComponent--small"
          href={shop_now_link}
        >
          {shop_now_title}
        </a>
      </div>
      <img
        alt="terracycle-icon"
        className="referralDashboard__terracycleIcon"
        src={terracycleIcon}
      />
    </div>
  )

  const StoreCreditExpirationBlock = () => (
    <>
      {expiring_store_credit_referral_due_date && (
        <h4>{expiring_store_credit_referral_amount}</h4>
      )}
      <div className="creditStore__hint">
        {expiring_store_credit_referral_due_date
          ? `${store_credits_expiring} ${expiring_store_credit_referral_due_date}`
          : none_store_credit_expiring}
        <button
          className="tooltip-btn icon-tooltip-info ms-3"
          data-bs-toggle="tooltip"
          title={store_credit_expiring_hint}
          type="button"
        />
      </div>
    </>
  )

  const SocialSection = () => (
    <div className="socialIcons">
      <h5 className="mb-0">
        <b>{social_media_share_link_title}</b>
      </h5>
      <ul className="socialIcons_list">
        <li className="socialIcons_item">
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${user_referral_link}`}
            rel="noreferrer"
            target="_blank"
          >
            <i className="icon-facebook" />
          </a>
        </li>
        <li className="socialIcons_item">
          <a
            href={`https://x.com/intent/post?url=${user_referral_link}`}
            rel="noreferrer"
            target="_blank"
          >
            <i className="icon-X" />
          </a>
        </li>
        <li className="socialIcons_item">
          <a
            href={`mailto:?body=${user_referral_link}`}
            rel="noreferrer"
            target="_blank"
          >
            <i className="icon-email" />
          </a>
        </li>
      </ul>
    </div>
  )

  const MobileShareButton = () => (
    <button
      className="buttonComponent--secondary buttonComponent--small mobileShare_button w-100"
      onClick={handleMobileShare}
      type="button"
    >
      {social_media_share_link_title}
      <i className="icon-share ms-2 me-0" />
    </button>
  )

  return (
    <div className="referralDashboard">
      <div className="row">
        {userIsReferrer ? (
          <>
            <div className="col-xl-6">
              <div className="columnLeftSide">
                <StoreCreditBlock />
                <div className="d-xl-flex">
                  <div className="columnLeftSide--small-block">
                    <StoreCreditExpirationBlock />
                  </div>
                  <div className="columnLeftSide--small-block">
                    <h4>{completed_referral_orders}</h4>
                    {completed_referrals_order_count_title}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="columnRightSide">
                <h4>{statistic_section_title}</h4>
                <p>{statistic_section_content}</p>
                <div className="bootstrapForm d-xl-flex align-items-end">
                  <div className="form-group me-3 p-0 w-100">
                    <label
                      className="control-label"
                      htmlFor="user_referral_link_id"
                    >
                      {your_referral_link_title}
                    </label>
                    <input
                      className="form-control"
                      id="user_referral_link_id"
                      name="user_referral_link"
                      readOnly
                      ref={inputRef}
                      type="text"
                      value={user_referral_link}
                    />
                  </div>
                  <button
                    className="buttonComponent--primary buttonComponent--small copyLink__button"
                    disabled={copied}
                    onClick={handleCopy}
                    type="button"
                  >
                    {copy_link}
                    <i className="icon-copy ms-2 me-0" />
                  </button>
                </div>
                {showSocialSection && <SocialSection />}

                {useShareButton && (
                  <div className="d-block d-xl-none">
                    <MobileShareButton />
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="col-xl">
            <div className="columnLeftSide">
              <StoreCreditBlock />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

ReferralInfo.propTypes = {
  translations: PropTypes.object.isRequired,
  userData: PropTypes.object.isRequired,
  userIsReferrer: PropTypes.bool.isRequired,
}
